import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, ExternalLink, Radar } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { API } from '../../App';

const ORANGE = '#ee7203';

/**
 * Live-tracking modal opened from a vehicle's tracking icon (set via Settings > Vehicles >
 * Live Tracking Link, e.g. a Samsara fleet-viewer URL). Before ever rendering the iframe, it
 * asks the backend whether the target URL even allows being embedded (the same
 * check-embeddable endpoint used by the Management Tools custom-URL modal) — if not, it skips
 * the iframe entirely and opens a real new tab automatically, no click required.
 *
 * Sizing: `rect` (when provided by TransportNewUI) is a measured {top,left,right,bottom}
 * gap between the Edit Mode + Map layout's 2 side rails — the modal is positioned to exactly
 * fill that gap so both rails stay visible/usable around it. When `rect` is null (every other
 * layout, e.g. the View All page), it renders as a bigger, centered full modal instead.
 */
const VehicleTrackingModal = ({ vehicle, url, rect, onClose }) => {
  const [checking, setChecking] = useState(true);
  const [showEmbedHint, setShowEmbedHint] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setChecking(true);
    api.get(`${API}/management-tools/check-embeddable`, { params: { url } })
      .then((res) => {
        if (cancelled) return;
        if (res.data?.embeddable === false) {
          window.open(url, '_blank', 'noopener,noreferrer');
          toast.info(`${vehicle?.vehicle_registration || 'Tracking'} can't be embedded here — opened in a new tab`);
          onClose();
          return;
        }
        setChecking(false);
      })
      .catch(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  useEffect(() => {
    if (checking) return;
    setShowEmbedHint(false);
    const t = setTimeout(() => setShowEmbedHint(true), 3500);
    return () => clearTimeout(t);
  }, [checking]);

  if (checking) return null; // avoid a flash of the modal for URLs that are about to redirect

  const wrapperStyle = rect
    ? { position: 'fixed', top: rect.top + 8, left: rect.left + 8, right: rect.right + 8, bottom: rect.bottom + 8, zIndex: 100 }
    : {};

  return createPortal((
    <div
      className={rect ? 'flex' : 'fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4'}
      style={wrapperStyle}
      onClick={rect ? undefined : onClose}
      data-testid="vehicle-tracking-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl flex flex-col w-full h-full overflow-hidden"
        style={rect ? {} : { width: '95vw', height: '92vh', maxWidth: '1400px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-2.5 border-b flex-shrink-0" style={{ background: `linear-gradient(135deg, ${ORANGE}, #d66803)` }}>
          <div className="flex items-center gap-2 min-w-0">
            <Radar className="w-4 h-4 text-white flex-shrink-0" />
            <span className="font-bold text-white text-sm truncate">
              Live Tracking — {vehicle?.vehicle_registration || vehicle?.driver_name || 'Vehicle'}
            </span>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white hover:bg-white/20 rounded-full p-1.5 transition-colors"
              title="Open in new tab"
              data-testid="vehicle-tracking-open-new-tab"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 rounded-full p-1.5 transition-colors"
              data-testid="vehicle-tracking-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        {showEmbedHint && (
          <div
            className="flex items-center justify-between gap-3 px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-800 flex-shrink-0"
            data-testid="vehicle-tracking-embed-hint"
          >
            <span>Not displaying correctly? This tracking page may not allow embedding.</span>
            <a href={url} target="_blank" rel="noopener noreferrer" className="font-semibold underline whitespace-nowrap">
              Open in a new tab
            </a>
          </div>
        )}
        <iframe
          src={url}
          className="flex-1 w-full border-0"
          title={`${vehicle?.vehicle_registration || 'Vehicle'} live tracking`}
          allow="geolocation"
        />
      </div>
    </div>
  ), document.body);
};

export default VehicleTrackingModal;
